// useDataDogRUM.ts
import { datadogRum } from '@datadog/browser-rum';
import { ref } from 'vue';

// Configuration interface for DataDog RUM
interface DataDogConfig {
    applicationId: string;
    clientToken: string;
    site?: string;
    service: string;
    env: string;
    version?: string;
    sessionSampleRate?: number;
    sessionReplaySampleRate?: number;
    trackUserInteractions?: boolean;
    trackResources?: boolean;
    trackLongTasks?: boolean;
    defaultPrivacyLevel?: 'allow' | 'mask' | 'mask-user-input';
}

export function useDataDogRUM() {
    let isDisabled = false;

    // Initialize DataDog RUM
    const init = (config: DataDogConfig) => {
        if (isDisabled) {
            console.warn('DataDog RUM is disabled.');
            return;
        }

        // default to datadoghq.com if no site is provided
        const site = config.site || 'datadoghq.com';
        // console.log('DataDog RUM config', config);

        try {
            datadogRum.init({
                applicationId: config.applicationId,
                clientToken: config.clientToken,
                site: site as any,
                service: config.service,
                env: config.env,
                version: config.version || '1.0.0',
                sessionSampleRate: config.sessionSampleRate ?? 100,
                sessionReplaySampleRate: config.sessionReplaySampleRate ?? 20,
                trackUserInteractions: config.trackUserInteractions ?? true,
                trackResources: config.trackResources ?? true,
                trackLongTasks: config.trackLongTasks ?? true,
                defaultPrivacyLevel: config.defaultPrivacyLevel ?? 'mask',
                // Intentional cancels (logout / navigate-away) must not pollute error tracking.
                beforeSend: (event) => {
                    if (event.type !== 'error') return true;
                    const message = event.error?.message ?? '';
                    if (
                        message.includes('AbortError') ||
                        message.includes('signal is aborted')
                    ) {
                        return false;
                    }
                    return true;
                },
            });

            if (config.sessionReplaySampleRate && config.sessionReplaySampleRate > 0) {
                datadogRum.startSessionReplayRecording();
            }

        } catch (error) {
            console.error('Failed to initialize DataDog RUM:', error);
        }
    };

    // Set user information
    const setUser = (user: { id: string; name?: string; email?: string, org_id?: string, org_name?: string }) => {
        if (isDisabled) {
            return;
        }
        try {
            datadogRum.setUser(user);
        } catch (datadogError) {
            console.warn('Failed to set user in DataDog RUM:', datadogError);
        }
    };

    const clearUser = () => {
        if (isDisabled) {
            return;
        }
        try {
            datadogRum.clearUser();
        } catch (datadogError) {
            console.warn('Failed to clear user in DataDog RUM:', datadogError);
        }
    };

    // Track a custom event
    const addAction = (name: string, context?: Record<string, any>) => {
        if (isDisabled) {
            return;
        }
        try {
            datadogRum.addAction(name, context);
        } catch (datadogError) {
            console.warn('Failed to send action to DataDog RUM:', datadogError);
        }
    };

    // Track a custom error
    const addError = (error: Error, context?: Record<string, any>) => {
        if (isDisabled) {
            return;
        }
        try {
            datadogRum.addError(error, context);
        } catch (datadogError) {
            console.warn('Failed to send error to DataDog RUM:', datadogError);
            console.error('Original error that failed to log:', error);
        }
    };

    // Track a page view
    const trackPageView = (path: string, name?: string) => {
        if (isDisabled) {
            return;
        }
        try {
            datadogRum.startView({
                name: name || path,
                service: datadogRum.getInitConfiguration()?.service,
                context: {
                    route: { path, name },
                }
            });
        } catch (datadogError) {
            console.warn('Failed to track page view in DataDog RUM:', datadogError);
        }
    };

    const disable = () => {
        isDisabled = true;
    };

    const enable = () => {
        isDisabled = false;
    };

    return {
        init,
        setUser,
        clearUser,
        addAction,
        addError,
        trackPageView,
        disable,
        enable,
    };
}
